import L, { CircleMarker, IconOptions, LatLng, LatLngBounds, latLngBounds, LatLngBoundsExpression, LatLngExpression, LatLngTuple, Marker, Point } from "leaflet";
import { result } from "lodash";
import { MarkerInfo } from "../components/search/map/map";
import { continentsCentroidsAndZooms } from "./centralStore";
import isTouchScreen from "./isTouchScreen";
import { WeatherSearchResultRowComplete } from "./weatherSearch";

export const initMap = ({ fromMainSearchForm, continent, zoom, center, mapId }: { fromMainSearchForm: boolean, continent: string, zoom?: number, center?: number[], mapId: string }) => {

    const continentValues = continentsCentroidsAndZooms['all'];
    const map = L.map(mapId, {
        attributionControl: false,
        zoomControl: false,
        scrollWheelZoom: fromMainSearchForm,
        boxZoom: false,
        touchZoom: fromMainSearchForm,
        doubleClickZoom: fromMainSearchForm,
        dragging: fromMainSearchForm,
        center: center as LatLngTuple || continentValues.center,
        maxBounds: [[80, -360], [-80, 360]],
        worldCopyJump: true,
        minZoom: 2,
        maxZoom: 7,
        tapTolerance: 25,
        zoom: (zoom && !isNaN(zoom)) ? zoom : continentValues.zoom
    });
    L.tileLayer("/tiles/{z}/{x}/{y}.jpg", {
        zoomOffset: -1,
        tileSize: 512,
        keepBuffer: 4
    }).addTo(map);
    return map;
}

export const setCircleHoverStyle = (marker: CircleMarker, hovered: boolean) => {
    marker.setStyle({ color: hovered ? "#0000FF" : "#E84749" });
}




export const generateCircles = ({ size, centroid, map }: { size: number, centroid: [number, number], map: L.Map }) => {
    const addedCircles: L.Circle[] = [];
    centroid = [centroid[1], centroid[0]];
    const touchScreen = isTouchScreen();
    let mainCircle = new L.CircleMarker(centroid, {
        radius: touchScreen ? 7 : 6,
        fillColor: "#FFF",
        fillOpacity: 0.75,
        color: "#E84749",
        weight: 2,
        className: "shadow-standard",
        stroke: true,

        fill: true
    }).addTo(map);
    const sizes = [[60000, 0.65], [100000, 0.55], [150000, 0.45], [230000, 0.35], [330000, 0.22]]
    // for (let i = 0; i < sizes.length; i++) {
    //     if (size > i + 1) {
    //         const [radius, opacity] = sizes[i]
    //         const circle = new L.Circle(centroid, {
    //             radius: radius,
    //             color: "#E84749",
    //             stroke: false,
    //             opacity: opacity,
    //             fill: true
    //         }).addTo(map);
    //         addedCircles.push(circle);
    //     }
    // }
    return { mainCircle, addedCircles }
}

export const setImageHoverStyle = (marker: Marker, hovered: boolean) => {
    const icon = marker.getIcon();
    const iconOptions = icon.options as IconOptions;
    let clsName = icon.options.className;
    if (hovered) {
        clsName = clsName.replace('shadow-map-image', 'hovered-map-image')
    } else {
        clsName = clsName.replace('hovered-map-image', 'shadow-map-image')
    }
    const newIcon = L.icon({
        ...iconOptions,
        className: clsName
    })
    marker.setIcon(newIcon);
}

export const generateImage = ({ centroid, map, url, size, toBeHidden }: { centroid: [number, number], map: L.Map, url: string, size: number, toBeHidden?: boolean }) => {
    centroid = [centroid[1], centroid[0]];
    let marker = new L.Marker(centroid, {

        icon: L.icon({

            iconUrl: url,
            tooltipAnchor: toBeHidden ? [10000, 10000] : [0, 0],
            iconSize: toBeHidden ? [0, 0] : [size, size],
            className: 'rounded-md transition-opacity duration-300 shadow-map-image'
        })
    }).addTo(map);
    return marker;
}

export const filterByBbox = ({ bounds, results }: { bounds: string, results: WeatherSearchResultRowComplete[] }) => {
    const boundsSplitted = bounds ? bounds.split(',').map(v => parseFloat(v)) : [];
    if (!boundsSplitted.length) {
        return results;
    }
    const lBounds = latLngBounds([boundsSplitted[1], boundsSplitted[0]] as LatLngTuple, [boundsSplitted[3], boundsSplitted[2]] as LatLngTuple);
    const boundsExtended = lBounds.pad(0.03);
    return results.filter(r => {
        const centroid: LatLngExpression = [r.centroid[1], r.centroid[0]];
        return boundsExtended.contains(centroid);
    })


}

export const getLatLngBounds = (bounds: LatLngBoundsExpression): L.LatLngBounds => {
    return L.latLngBounds(bounds[0], bounds[1])
}

export const getMarkersEligibleForTooltip = (results: WeatherSearchResultRowComplete[], zoom: number): WeatherSearchResultRowComplete[] => {
    const latLim = zoom == 6 ? 4 : 7;
    const lonLim = zoom == 6 ? 2 : 3.5;
    return results.filter(r => {
        for (let other of results) {
            if (r == other) continue;
            const lonDiff = Math.abs(r.centroid[0] - other.centroid[0]) / Math.cos(r.centroid[1] / 180 * Math.PI);
            const latDiff = Math.abs(r.centroid[1] - other.centroid[1]);
            if (lonDiff < lonLim && latDiff < latLim) return false;
        }
        return true;
    })
}

export const closeAllMarkers = (markers: { [key: string]: MarkerInfo }) => {
    for (let key in markers) {
        const marker = markers[key].marker;
        marker.closeTooltip();
        marker.unbindTooltip();
    }
}

export const showMarkersInArea = (results: WeatherSearchResultRowComplete[]) => {
    const latLim = 2;
    const lonLim = 5;
    for (let r of results) {
        for (let other of results) {
            if (r == other) continue;
            const lonDiff = Math.abs(r.centroid[0] - other.centroid[0]) / Math.cos(r.centroid[1] / 180 * Math.PI);
            const latDiff = Math.abs(r.centroid[1] - other.centroid[1]);
            if (lonDiff < lonLim && latDiff < latLim) return false;
        }
    }
    return true;
}

export const findAppropriateMarker = (event: L.LeafletMouseEvent, map: L.Map, results: WeatherSearchResultRowComplete[]) => {
    const eventPoint: Point = event.layerPoint;
    for (let r of results) {
        const markerPoint = map.latLngToLayerPoint([r.centroid[1], r.centroid[0]]);
        const dist = eventPoint.distanceTo(markerPoint);
        if (dist < 25) {
            return r.id;
        }
    }
    return null;
}
